import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { TooltipModule } from '@amin-karimi/ng2-tooltip-directive';
import { DeviceService } from '@omni/services/device/device.service';
import { AuthenticationService } from '@omni/services/authentication.service';
import { NavigationService } from '@omni/services/navigation/navigation.service';

interface ButtonConfig {
  id: string;
  icon: string;
  isDisabled: boolean;
  align: 'left' | 'right';
}

const DefaultTitleWidth = '100%';

/**
 * Right-pane page header: same behavior and toolbar contract as IndPageTitleComponent,
 * with an extra **trailing** region for content projection. Projected nodes must use the
 * `indPageHeaderActions` attribute (e.g. split-button CTAs that are not representable as
 * `viewData.controls` icon rows).
 */
@Component({
  selector: 'ind-page-header',
  templateUrl: 'ind-page-header.html',
  styleUrls: ['ind-page-header.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, IonicModule, TooltipModule],
})
export class IndPageHeaderComponent implements OnInit, OnChanges, AfterViewInit {
  readonly buttonsFilter = {
    left: { align: 'left' as const },
    right: { align: 'right' as const }
  };
  readonly defaultTitleWidth = DefaultTitleWidth;

  @Input() viewData: any;
  @Input() isSkeleton: boolean = false;
  @Input() skeletonConfig: { titleWidth: string } = { titleWidth: DefaultTitleWidth };

  @Output() onControlClick = new EventEmitter<string>();
  @Output() onFilterClick = new EventEmitter<string>();
  enableBackButton = false;
  readonly backButton: ButtonConfig = {
    id: 'close',
    icon: 'chevron-back-outline',
    isDisabled: false,
    align: 'left'
  };

  rightControls: ButtonConfig[] = [];
  leftControls: ButtonConfig[] = [];
  isAndroid: boolean = false;
  isHCPFacing = false;

  constructor(
    public device: DeviceService,
    public authService: AuthenticationService,
    public navService: NavigationService,
    private _cd: ChangeDetectorRef) { }

  backgroundUploadInProgress$ = this.device.isBackgroundUploadInProgressObservable;
  syncInProgress$ = this.device.syncInProgress$;

  ngOnInit() {
    this.isAndroid = this.device.isAndroid();
    if (this.viewData && !this.viewData.controls) this.viewData.controls = [];
    if (this.viewData && this.viewData.isHCPFacing) this.isHCPFacing = true;
    this.enableBackButton = this.isDefaultBackButtonReq();
    this.leftControls = this.viewData?.controls?.filter((btn) => btn.align === 'left') || [];
    this.rightControls = this.viewData?.controls?.filter((btn) => btn.align === 'right') || [];
  }

  ngAfterViewInit() {
    const elements = document.getElementsByTagName('tooltip');
    for (let index = elements.length - 1; index >= 0; index--) {
      elements[index].parentNode.removeChild(elements[index]);
    }
  }

  ngOnChanges() {
    if (this.viewData && !this.viewData.controls)
      this.viewData.controls = [];
    this.enableBackButton = this.isDefaultBackButtonReq();
    this.leftControls = this.viewData?.controls?.filter((btn) => btn.align === 'left') || [];
    this.rightControls = this.viewData?.controls?.filter((btn) => btn.align === 'right') || [];
  }

  private isDefaultBackButtonReq(): boolean {
    if (this.viewData && !this.viewData.controls) {
      this.viewData.controls = [];
    }
    if (this.viewData && this.viewData.id == 'veeva-widget') return true;
    if (this.viewData?.displayDefaultBackButton) return true;
    return (this.viewData && this.viewData.id !== "new-activity-page-title"
    && this.viewData.id !== "capture-allocation-order"
    && this.viewData.id !== "presentation-fullscreen"
    && this.viewData.id !== 'new-insights-plus-header'
    && this.viewData.id !== 'new-change-req-page-title'
    && this.viewData.id !== 'general-insights-page-title'
    && this.viewData.id !== 'customer-insight'
    && this.viewData.id !== 'customer-insight-journey'
    && this.viewData.id !== 'customer-relationship'
    && this.viewData.id !== 'dynamic-form-page-title'
    && this.viewData.id !== 'section-specific'
    && this.viewData.id !== 'new-manage-list-title'
    && this.viewData.id !== 'photo-capture-viewer-page-title'
    && this.viewData.controls.filter(control => control.align === this.backButton.align).length === 0)
      && (window.innerWidth < 620);
  }

  getButtonClasses(button: { id?: string; name?: string; cssClass?: string }): { [key: string]: boolean } {
    const classes: { [key: string]: boolean } = { 'has-label': !!button.name };
    if (button.cssClass) classes[button.cssClass] = true;
    if (button.id === 'signature-captured') classes['signature-captured-icon'] = true;
    return classes;
  }

  public onButtonClick(button) {
    if (button.id && !button.isDisabled) {
      this.onControlClick.emit(button.id);
    }
  }

  @HostListener('window:resize', ['$event'])
  onresize(_event: Event) {
    this.enableBackButton = this.isDefaultBackButtonReq();
  }

  public filterClicked() {
    if (this.viewData.isFilter) {
      this.onFilterClick.emit();
    }
  }

  onTooltipVisibilityChange() {
    this._cd.markForCheck();
  }

  trackById(index: number, item: any) { return item?.id || item?.ID || index; }
}
